import React, { ReactNode, SyntheticEvent } from 'react';
import {
  IconButton as MUIIconButton,
  IconButtonProps as MUIIconButtonProps,
  SxProps,
  Tooltip as MUITooltip,
} from '@mui/material';
import * as R from 'ramda';

import colors from '@atom/styles/colors';

type CustomProps = {
  /**
   * optional styles to be applied to the root
   */
  style?: React.CSSProperties;
  /**
   * optional text for button tooltip
   */
  tooltip?: NonNullable<React.ReactNode>;
  /**
   * lightTooltip will change basic styles for tooltip to
   * be a white background card
   */
  lightTooltip?: boolean;
  /**
   * Use maxWidth to increase or decrease tooltip width
   * - used to prevent wrapping long text
   */
  maxWidth?: number;
};

type AtomMUIButtonProps = {
  component?: any;
  size?: 'small' | 'medium' | 'large';
  href?: string;
  target?: HTMLAnchorElement['target'];
  classes?: any;
  children?: ReactNode;
  onClick?: (event: SyntheticEvent<any>) => void;
  disabled?: boolean;
  edge?: false | 'end' | 'start';
  disableRipple?: boolean;
  disableFocusRipple?: boolean;
  disableTouchRipple?: boolean;
  type?: 'button' | 'reset' | 'submit';
  sx?: any;
  'aria-label'?: string;
  'data-cy'?: string;
};

type AtomMUITooltipProps = {
  TooltipProps?: any;
};

export type IconButtonProps = CustomProps &
  AtomMUIButtonProps &
  AtomMUITooltipProps;

export const IconButton = (props: IconButtonProps) => {
  const {
    size = 'large',
    tooltip = '',
    lightTooltip = false,
    TooltipProps = {},
    maxWidth,
    ...data
  } = props;

  const sx: SxProps = {
    ...props.style,
  };

  const lightTooltipSx: SxProps = {
    backgroundColor: colors.neutral.white,
    color: colors.neutral.black,
    boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2)',
    fontSize: 11,
    fontWeight: 'normal',
  };

  const MUIButtonProps: MUIIconButtonProps = R.omit(['style'], data);
  if (!tooltip) {
    return <MUIIconButton {...MUIButtonProps} size={size} sx={sx} />;
  }

  return (
    <MUITooltip
      title={tooltip}
      slotProps={{
        tooltip: {
          sx: {
            ...(lightTooltip ? lightTooltipSx : {}),
            ...(maxWidth !== undefined ? { maxWidth } : {}),
          },
        },
      }}
      {...TooltipProps}
    >
      <span>
        <MUIIconButton {...MUIButtonProps} size={size} sx={sx} />
      </span>
    </MUITooltip>
  );
};
